/**
 * Carries PostHog experiment assignments into checkout.
 *
 * Checkout UI extensions run in a sandboxed Web Worker: no DOM, no third-party
 * scripts, so posthog-js cannot run there and cannot be asked which variant a
 * buyer is in. Cart attributes are the one channel that crosses the boundary —
 * they reach an already-open checkout and they land on the order as
 * note_attributes. Same mechanism the Packeta picker relies on; see
 * docs/checkout/parcelshop-selectors.md.
 *
 * So: every active flag whose key starts with `chk-` is written to the cart as
 * `_ph_f_<flag-key>`. A checkout extension reads shopify.attributes and needs no
 * plumbing of its own, and a future test appears there for free.
 *
 * ASSIGNMENT TIME IS NOT EXPOSURE TIME. This runs as soon as the cart is
 * non-empty, which is deliberately earlier than "the buyer is in the experiment".
 * Doing it on the checkout button click instead — the intuitive choice — would
 * put a flags round-trip and a cart write in front of the highest-intent click
 * on the site, and a buyer who raced it would arrive at checkout with no
 * variant. Who actually counts is decided by the experiment's exposure event
 * (`checkout_started`, fired by the pixel), so assigning early costs nothing:
 * anyone who abandons before checkout never enters the experiment.
 *
 * getAllFeatureFlags() rather than getFeatureFlag(): the latter emits a
 * `$feature_flag_called` event per call, which would be both noise against the
 * custom exposure event and a charge against the free plan's event budget.
 */
(function () {
  const CONFIG_SELECTOR = '[data-gb-experiment-config]';
  const DISTINCT_ID_ATTRIBUTE = '_ph_did';

  const config = readConfig();
  if (!config.cartUpdateUrl) return;

  /**
   * Our attributes as we last knew them. Seeded from the server-rendered cart,
   * then kept current by this script — nothing else writes `_ph_*`, so there is
   * no need to re-read the cart to stay accurate.
   * @type {Record<string, string>}
   */
  const known = ownAttributes(config.attributes || {});

  let itemCount = Number(config.itemCount) || 0;
  let listening = false;
  let writing = false;

  if (itemCount > 0) sync();

  /**
   * An add-to-cart updates the drawer by section morph, with no page load, and
   * the buyer can go straight from that drawer to checkout. Without this the
   * most common path of all would reach checkout unassigned.
   */
  document.addEventListener('shopify:cart:lines-update', (event) => {
    // The updated cart arrives on event.promise, not synchronously on detail -
    // see how cart-icon.js consumes the same event. When it is unavailable,
    // assume there is now something in the cart: a lines-update is almost
    // always an add, and sync() is a no-op when nothing has changed anyway.
    if (event?.promise?.then) {
      event.promise.then(({cart}) => applyCart(cart)).catch(() => applyCart(null));
      return;
    }
    applyCart(null);
  });

  /** @param {{totalQuantity?: number} | null} cart */
  function applyCart(cart) {
    const total = Number(cart?.totalQuantity);
    itemCount = Number.isFinite(total) ? total : Math.max(itemCount, 1);
    if (itemCount > 0) sync();
  }

  /** @returns {Record<string, any>} */
  function readConfig() {
    const node = document.querySelector(CONFIG_SELECTOR);
    try {
      return node ? JSON.parse(node.textContent || '{}') : {};
    } catch {
      return {};
    }
  }

  /**
   * @param {Record<string, string>} attributes
   * @returns {Record<string, string>}
   */
  function ownAttributes(attributes) {
    /** @type {Record<string, string>} */
    const own = {};
    for (const [key, value] of Object.entries(attributes)) {
      if (key === DISTINCT_ID_ATTRIBUTE || key.startsWith(config.attributePrefix)) {
        own[key] = String(value);
      }
    }
    return own;
  }

  function sync() {
    // No test running (`ph_experiments` in gb-posthog.liquid): ask PostHog for
    // nothing, but still take old assignments off carts that carry one, so a
    // stopped test cannot outlive the switch.
    if (!config.experiments) {
      write(cleared());
      return;
    }

    if (typeof window.posthog === 'undefined') return;

    // onFeatureFlags is a persistent subscription, not a one-shot: it fires on
    // every flag load for the life of the page. Registering it inside sync()
    // would stack a new listener per cart change, and each of them would run on
    // the next load. Bind once; sync() only has to ask for a refresh.
    if (!listening) {
      listening = true;
      window.posthog.onFeatureFlags(() => {
        write(desiredAttributes());
      });
    }

    // Flags are not fetched on page load (see gb-posthog.liquid), so ask for
    // them here. This is also what picks up a flag that was enabled, ramped or
    // stopped since the page was rendered.
    window.posthog.reloadFeatureFlags();
  }

  /**
   * Every `_ph_f_` attribute we know of, set to '' - which is how Shopify
   * deletes a cart attribute.
   * @returns {Record<string, string>}
   */
  function cleared() {
    /** @type {Record<string, string>} */
    const desired = {};
    for (const key of Object.keys(known)) {
      if (key.startsWith(config.attributePrefix)) desired[key] = '';
    }
    return desired;
  }

  /**
   * What the cart should carry right now.
   *
   * A flag that has gone away — test stopped, or the buyer no longer matches its
   * release conditions — resolves to an empty string, which is how Shopify
   * deletes a cart attribute. That is what lets stopping a test in the PostHog
   * UI reach carts that already exist.
   *
   * @returns {Record<string, string>}
   */
  function desiredAttributes() {
    const flags = readFlags();

    // null means posthog-js is not actually here - blocked by an ad blocker, or
    // still the stub snippet. Bail out rather than compute an empty desired
    // state, which would clear a perfectly good assignment off the cart.
    if (!flags) return null;

    const desired = cleared();

    let active = false;
    for (const [key, value] of Object.entries(flags)) {
      if (!key.startsWith(config.flagPrefix) || value === false) continue;
      desired[config.attributePrefix + key] = String(value);
      active = true;
    }

    // The distinct_id is only useful to the pixel while a test is running, and
    // it is written once rather than mirrored: in cookieless mode the hash can
    // change between sessions, and re-writing it on every visit would churn the
    // cart for no gain.
    if (active && !known[DISTINCT_ID_ATTRIBUTE]) {
      const distinctId = window.posthog.get_distinct_id();
      if (distinctId) desired[DISTINCT_ID_ATTRIBUTE] = String(distinctId);
    }

    return desired;
  }

  /**
   * Every flag PostHog is serving this visitor, as a flat {key: variant} map.
   *
   * `getAllFeatureFlags()` returns an ARRAY of {key, enabled, variant, payload},
   * not a map - getting that wrong is silent, because iterating an array with
   * Object.entries yields "0", "1", ... which match no flag prefix and write
   * nothing at all. Older posthog-js returned a map, so both shapes are
   * normalised here rather than pinning a version.
   *
   * Returns null when posthog-js is not really present. The snippet installs a
   * stub with a fixed method list that does NOT include getAllFeatureFlags, so
   * when an ad blocker eats the real library this is how we notice.
   *
   * @returns {Record<string, string | boolean> | null}
   */
  function readFlags() {
    const ph = window.posthog;
    if (typeof ph?.getAllFeatureFlags !== 'function') return null;

    let raw;
    try {
      raw = ph.getAllFeatureFlags();
    } catch {
      return null;
    }
    if (!raw) return null;

    /** @type {Record<string, string | boolean>} */
    const flags = {};
    if (Array.isArray(raw)) {
      for (const flag of raw) {
        if (!flag?.key) continue;
        flags[flag.key] = flag.variant ?? Boolean(flag.enabled);
      }
    } else if (typeof raw === 'object') {
      Object.assign(flags, raw);
    }
    return flags;
  }

  /**
   * @param {Record<string, string>} desired
   * @returns {Promise<void>}
   */
  async function write(desired) {
    if (!desired) return;

    /** @type {Record<string, string>} */
    const changed = {};
    for (const [key, value] of Object.entries(desired)) {
      if ((known[key] || '') !== value) changed[key] = value;
    }
    if (writing || Object.keys(changed).length === 0) return;
    writing = true;

    try {
      const response = await fetch(config.cartUpdateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: changed }),
      });
      if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);

      for (const [key, value] of Object.entries(changed)) {
        if (value === '') delete known[key];
        else known[key] = value;
      }
    } catch {
      // A buyer who cannot be assigned simply sees the control experience. Never
      // let an analytics write break the cart.
    } finally {
      writing = false;
    }
  }
})();
